import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import PublicBrowseShell from '../../components/public/PublicBrowseShell';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { matchService } from '../../services/matchService';
import { tournamentService } from '../../services/tournamentService';
import { isCompetitionPubliclyVisible } from '../../utils/publicCompetitionVisibility';

const TYPE_OPTIONS = [
  { value: 'all', label: 'All Competitions' },
  { value: 'league', label: 'League Matches' },
  { value: 'tournament', label: 'Tournament Games' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'live', label: 'Live Now' },
  { value: 'scheduled', label: 'Upcoming' },
  { value: 'completed', label: 'Final' },
];

const formatWhen = (iso) => {
  if (!iso) return 'TBA';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'TBA';
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const normalizeLeagueMatch = (match) => ({
  id: `league-${match.id}`,
  sourceId: match.id,
  competitionType: 'league',
  competitionLabel: match.league?.name
    ? `${match.league.name}${match.league.season ? ` · ${match.league.season}` : ''}`
    : 'League',
  homeName: match.homeTeam?.name || match.homeTeam?.shortName || 'Home',
  awayName: match.awayTeam?.name || match.awayTeam?.shortName || 'Away',
  homeLogo: match.homeTeam?.logoUrl,
  awayLogo: match.awayTeam?.logoUrl,
  homeScore: match.homeScore,
  awayScore: match.awayScore,
  status: match.matchStatus || 'scheduled',
  scheduledDate: match.scheduledDate,
  venue: match.venue || 'TBD',
  competitionMeta: match.league
    ? {
        endDate: match.league.endDate,
        leagueStatus: match.league.leagueStatus,
      }
    : null,
});

const normalizeTournamentGame = (game, tournamentById = {}) => {
  const tournament = tournamentById[game.tournamentId] || null;
  return {
    id: `tournament-${game.id}`,
    sourceId: game.id,
    competitionType: 'tournament',
    competitionLabel: tournament?.name || game.stageName || 'Tournament',
    homeName: game.homeTeam?.teamName || 'Home',
    awayName: game.awayTeam?.teamName || 'Away',
    homeLogo: game.homeTeam?.logoUrl,
    awayLogo: game.awayTeam?.logoUrl,
    homeScore: game.homeScore,
    awayScore: game.awayScore,
    status: game.gameStatus || 'scheduled',
    scheduledDate: game.scheduledDate,
    venue: game.venue || 'TBD',
    competitionMeta: tournament,
  };
};

const statusTone = (status) => {
  if (status === 'live') return 'bg-error/10 text-error';
  if (status === 'completed') return 'bg-success/10 text-success';
  if (status === 'postponed') return 'bg-warning/10 text-warning';
  if (status === 'cancelled') return 'bg-muted text-muted-foreground';
  return 'bg-primary/10 text-primary';
};

const MatchCenter = () => {
  const [competitionType, setCompetitionType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [games, setGames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadGames = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [leagueMatches, tournamentGames, tournaments] = await Promise.all([
        matchService.getAll().catch(() => []),
        tournamentService.getGames().catch(() => []),
        tournamentService.getTournaments().catch(() => []),
      ]);

      const tournamentById = Object.fromEntries(
        (tournaments || []).map((t) => [t.id, t])
      );

      const unified = [
        ...(leagueMatches || [])
          .filter((match) =>
            match.league
              ? isCompetitionPubliclyVisible({
                  endDate: match.league.endDate,
                  leagueStatus: match.league.leagueStatus,
                })
              : true
          )
          .map(normalizeLeagueMatch),
        ...(tournamentGames || [])
          .filter((game) => {
            if (!game.tournamentId) return true;
            const tournament = tournamentById[game.tournamentId];
            return tournament ? isCompetitionPubliclyVisible(tournament) : true;
          })
          .map((game) => normalizeTournamentGame(game, tournamentById)),
      ];
      setGames(unified);
    } catch (err) {
      console.error('Failed to load match center:', err);
      setError(err.message || 'Failed to load games.');
      setGames([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Match Center - Tanzania Basketball Federation';
    loadGames();
    const intervalId = window.setInterval(loadGames, 45000);
    return () => window.clearInterval(intervalId);
  }, [loadGames]);

  const filteredGames = useMemo(() => {
    let list = [...games];
    if (competitionType !== 'all') {
      list = list.filter((g) => g.competitionType === competitionType);
    }
    if (statusFilter !== 'all') {
      list = list.filter((g) => g.status === statusFilter);
    }
    list.sort((a, b) => {
      if (a.status === 'live' && b.status !== 'live') return -1;
      if (b.status === 'live' && a.status !== 'live') return 1;
      const dateA = new Date(a.scheduledDate || 0).getTime();
      const dateB = new Date(b.scheduledDate || 0).getTime();
      if (a.status === 'scheduled' && b.status === 'scheduled') return dateA - dateB;
      if (a.status === 'completed' && b.status === 'completed') return dateB - dateA;
      return dateB - dateA;
    });
    return list;
  }, [games, competitionType, statusFilter]);

  const analytics = useMemo(() => {
    const live = games.filter((g) => g.status === 'live');
    const scheduled = games.filter((g) => g.status === 'scheduled');
    const completed = games.filter(
      (g) =>
        g.status === 'completed' ||
        (g.homeScore != null && g.awayScore != null)
    );
    const scored = completed.filter(
      (g) => g.homeScore != null && g.awayScore != null
    );
    const totalPoints = scored.reduce(
      (sum, g) => sum + (Number(g.homeScore) || 0) + (Number(g.awayScore) || 0),
      0
    );
    const avgCombined = scored.length
      ? Number((totalPoints / scored.length).toFixed(1))
      : 0;
    const homeWins = scored.filter((g) => g.homeScore > g.awayScore).length;
    const awayWins = scored.filter((g) => g.awayScore > g.homeScore).length;
    const ties = scored.filter((g) => g.homeScore === g.awayScore).length;

    return {
      liveCount: live.length,
      upcomingCount: scheduled.length,
      completedCount: completed.length,
      avgCombined,
      homeWins,
      awayWins,
      ties,
      leagueCount: games.filter((g) => g.competitionType === 'league').length,
      tournamentCount: games.filter((g) => g.competitionType === 'tournament').length,
    };
  }, [games]);

  const liveGames = filteredGames.filter((g) => g.status === 'live');
  const otherGames = filteredGames.filter((g) => g.status !== 'live');

  const GameCard = ({ game }) => {
    const showScore =
      game.status === 'live' ||
      game.status === 'completed' ||
      (game.homeScore != null && game.awayScore != null);

    return (
      <div
        className={`bg-card border rounded-lg p-4 card-shadow ${
          game.status === 'live' ? 'border-error/40 ring-1 ring-error/20' : 'border-border'
        }`}
      >
        <div className="flex items-center justify-between gap-2 mb-3">
          <p className="text-xs uppercase tracking-wide text-primary font-semibold truncate">
            {game.competitionLabel}
          </p>
          <span
            className={`text-xs px-2 py-0.5 rounded-full capitalize shrink-0 ${statusTone(
              game.status
            )}`}
          >
            {game.status === 'live' && (
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-current mr-1 animate-pulse" />
            )}
            {game.status}
          </span>
        </div>

        <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
          <div className="text-right min-w-0">
            <p className="font-semibold text-foreground truncate">{game.homeName}</p>
          </div>
          <div className="text-center px-2">
            {showScore ? (
              <p className="text-2xl font-bold tabular-nums text-foreground">
                {game.homeScore ?? 0}
                <span className="mx-1 text-muted-foreground font-medium">–</span>
                {game.awayScore ?? 0}
              </p>
            ) : (
              <p className="text-sm font-medium text-muted-foreground">vs</p>
            )}
          </div>
          <div className="text-left min-w-0">
            <p className="font-semibold text-foreground truncate">{game.awayName}</p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Icon name="Clock" size={12} />
            {formatWhen(game.scheduledDate)}
          </span>
          <span className="inline-flex items-center gap-1 truncate">
            <Icon name="MapPin" size={12} />
            {game.venue}
          </span>
          <span className="capitalize">{game.competitionType}</span>
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Match Center - Tanzania Basketball Federation</title>
        <meta
          name="description"
          content="Live scores, upcoming fixtures, and match analytics for Tanzania Basketball Federation leagues and tournaments."
        />
      </Helmet>
      <PublicBrowseShell
        title="Match Center"
        subtitle="Live scores, fixtures, and public match analytics"
        actions={
          <button
            type="button"
            onClick={loadGames}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted"
          >
            <Icon name="RefreshCcw" size={14} />
            Refresh
          </button>
        }
      >
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-lg p-4 card-shadow">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Live Now</p>
              <p className="text-3xl font-bold text-error mt-1">{analytics.liveCount}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4 card-shadow">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Upcoming</p>
              <p className="text-3xl font-bold text-primary mt-1">{analytics.upcomingCount}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4 card-shadow">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Completed</p>
              <p className="text-3xl font-bold text-foreground mt-1">{analytics.completedCount}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4 card-shadow">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Avg Combined Pts</p>
              <p className="text-3xl font-bold text-foreground mt-1">{analytics.avgCombined}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-card border border-border rounded-lg p-4 card-shadow">
              <p className="text-sm text-muted-foreground">Home wins</p>
              <p className="text-2xl font-bold text-foreground">{analytics.homeWins}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4 card-shadow">
              <p className="text-sm text-muted-foreground">Away wins</p>
              <p className="text-2xl font-bold text-foreground">{analytics.awayWins}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4 card-shadow">
              <p className="text-sm text-muted-foreground">Competitions loaded</p>
              <p className="text-sm text-foreground mt-1">
                {analytics.leagueCount} league · {analytics.tournamentCount} tournament
              </p>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4 card-shadow">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Competition"
                options={TYPE_OPTIONS}
                value={competitionType}
                onChange={setCompetitionType}
              />
              <Select
                label="Status"
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={setStatusFilter}
              />
            </div>
          </div>

          {isLoading && (
            <div className="py-16 text-center text-muted-foreground">
              <Icon name="Loader2" size={36} className="mx-auto mb-3 animate-spin text-primary" />
              Loading live scores and fixtures...
            </div>
          )}

          {!isLoading && error && (
            <div className="py-12 text-center text-destructive space-y-2">
              <Icon name="AlertTriangle" size={28} className="mx-auto" />
              <p>{error}</p>
            </div>
          )}

          {!isLoading && !error && filteredGames.length === 0 && (
            <div className="py-12 text-center text-muted-foreground space-y-2">
              <Icon name="Calendar" size={28} className="mx-auto" />
              <p>No games match the selected filters.</p>
            </div>
          )}

          {!isLoading && !error && liveGames.length > 0 && (
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-error animate-pulse" />
                <h2 className="text-xl font-semibold text-foreground">Live Games</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {liveGames.map((game) => (
                  <GameCard key={game.id} game={game} />
                ))}
              </div>
            </section>
          )}

          {!isLoading && !error && otherGames.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-xl font-semibold text-foreground">
                {statusFilter === 'live' ? 'Other' : 'Fixtures & Results'}
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {otherGames.map((game) => (
                  <GameCard key={game.id} game={game} />
                ))}
              </div>
            </section>
          )}
        </div>
      </PublicBrowseShell>
    </>
  );
};

export default MatchCenter;
